import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ChevronRight, Loader2, Dog, ArrowLeft,
  Mountain, Shield, Flame, ArrowLeftRight, Snowflake,
  Search, Crosshair, Waves, Heart, Wind, Sparkles,
} from 'lucide-react';
import MarketplaceFooter from '@/components/MarketplaceFooter';
import TopNavBar from '@/components/TopNavBar';
import { API } from '@/utils/api';

const ICONS = {
  Mountain, Shield, Flame, ArrowLeftRight, Snowflake,
  Search, Crosshair, Waves, Heart, Wind, Sparkles,
};

export default function FciGroupPage() {
  const { groupSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/public/grupy-fci/${groupSlug}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [groupSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#05050f] text-white flex flex-col items-center justify-center gap-4 px-6">
        <h1 className="text-2xl font-bold">Grupa FCI nie znaleziona</h1>
        <button
          onClick={() => navigate('/grupy-fci')}
          className="text-emerald-400 hover:underline"
          data-testid="fci-group-back-btn"
        >
          ← Zobacz wszystkie grupy FCI
        </button>
      </div>
    );
  }

  const { group, breeds, breeds_total, breeds_with_kennels } = data;
  const IconComponent = ICONS[group.icon] || Dog;

  return (
    <div className="min-h-screen bg-[#05050f] text-white">
      <Helmet>
        <title>{group.full_name} — hodowle psów rasowych | ApisAI</title>
        <meta name="description" content={`${group.description} Sprawdź wszystkie rasy z ${group.roman} grupy FCI oraz hodowle FCI / ZKwP.`} />
        <meta property="og:title" content={`${group.full_name} — hodowle ZKwP`} />
        <meta property="og:description" content={group.description} />
        <link rel="canonical" href={`https://apisai.pl/grupy-fci/${group.slug}`} />
      </Helmet>

      <TopNavBar />

      {/* Breadcrumbs + hero */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <nav className="text-xs text-gray-500 mb-4 flex items-center gap-2 flex-wrap" data-testid="fci-group-breadcrumbs">
            <Link to="/katalog" className="hover:text-white">Marketplace</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/grupy-fci" className="hover:text-white">Grupy FCI</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-300">{group.name}</span>
          </nav>

          <Link to="/grupy-fci" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1 mb-6" data-testid="fci-group-back">
            <ArrowLeft className="w-4 h-4" />
            Wszystkie grupy FCI
          </Link>

          <div className="flex items-start gap-5">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-400/30 flex items-center justify-center shrink-0 shadow-xl shadow-emerald-500/5">
              <IconComponent className="w-10 h-10 md:w-12 md:h-12 text-emerald-400" strokeWidth={1.3} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-emerald-400/90 font-mono tracking-[0.2em] uppercase mb-2">
                {group.roman} Grupa FCI
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4" data-testid="fci-group-title">
                {group.name}
              </h1>
              <p className="text-base text-gray-300 leading-relaxed max-w-3xl">
                {group.description}
              </p>

              {group.traits && group.traits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {group.traits.map(trait => (
                    <span key={trait} className="text-xs px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full">
                      {trait}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-5 mt-6 text-sm text-gray-400">
                <span><strong className="text-white">{breeds_total}</strong> ras</span>
                {breeds_with_kennels > 0 && (
                  <span><strong className="text-emerald-400">{breeds_with_kennels}</strong> z hodowlami</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breeds grid */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-xl md:text-2xl font-bold mb-6">
          Rasy w grupie <span className="text-emerald-400">{group.roman}</span>
        </h2>

        {breeds.length === 0 ? (
          <div className="text-center py-12 text-gray-400 bg-[#0c0c1a]/50 border border-white/5 rounded-xl">
            Brak ras w tej grupie.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fci-group-breeds-grid">
            {breeds.map((b, i) => (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.01, 0.3) }}
              >
                <Link
                  to={`/rasy/${b.slug}`}
                  data-testid={`fci-breed-${b.slug}`}
                  className="flex items-center justify-between bg-[#0c0c1a]/80 border border-white/10 rounded-lg px-4 py-3 hover:border-emerald-400/40 hover:bg-[#0c0c1a] transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Dog className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 shrink-0" />
                    <span className="text-sm text-white truncate">{b.name}</span>
                  </div>
                  {b.kennels_count > 0 && (
                    <span className="text-[11px] text-emerald-400 font-semibold shrink-0 ml-2">
                      {b.kennels_count} hod.
                    </span>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <MarketplaceFooter />
    </div>
  );
}
